import { useEffect, useState } from 'react'
import { closeCard, createCard, listCards, type Card } from '../lib/api'

export function Cards() {
  const [cards, setCards] = useState<Card[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const reload = () =>
    listCards()
      .then((r) => setCards(r.cards))
      .catch((e) => setErr(e?.message || 'failed to load'))

  useEffect(() => {
    reload()
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-light text-zinc-100">cards</h1>
        <button
          onClick={() => setShowAdd((s) => !s)}
          className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-sm font-medium hover:bg-white"
        >
          {showAdd ? 'cancel' : '+ add card'}
        </button>
      </div>

      {showAdd && <AddCardForm onSaved={() => { setShowAdd(false); reload() }} />}

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {cards.length === 0 ? (
        <div className="border border-dashed border-zinc-900 rounded px-4 py-10 text-center text-zinc-600 text-sm">
          no cards yet.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-900 border border-zinc-900 rounded">
          {cards.map((c) => (
            <li key={c.id} className="px-4 py-3 flex items-start justify-between text-sm">
              <div>
                <div className="text-zinc-200">
                  {c.name}
                  {c.is_business && <span className="ml-2 text-[10px] text-zinc-500">BIZ</span>}
                  {c.status !== 'active' && (
                    <span className="ml-2 text-[10px] text-zinc-500 uppercase">{c.status}</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {c.issuer ?? '—'}
                  {c.points_program && ` · ${c.points_program}`}
                  {c.opened && ` · opened ${c.opened}`}
                  {c.apr_zero_pct_until && ` · 0% APR until ${c.apr_zero_pct_until}`}
                </div>
                {c.reward_categories.length > 0 && (
                  <div className="text-[11px] text-zinc-600 mt-1">
                    {c.reward_categories
                      .map((r) => `${r.multiplier}x ${r.cat}`)
                      .join(' · ')}
                  </div>
                )}
              </div>
              <div className="text-right">
                {c.credit_limit != null && (
                  <div className="text-zinc-400 text-[11px] num">
                    ${c.credit_limit.toLocaleString()}
                  </div>
                )}
                {c.status === 'active' && (
                  <button
                    onClick={() => closeCard(c.id).then(reload)}
                    className="text-[11px] text-zinc-600 hover:text-red-400 mt-2"
                  >
                    close
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AddCardForm({ onSaved }: { onSaved: () => void }) {
  const [form, setForm] = useState({
    card_key: '',
    name: '',
    issuer: '',
    points_program: '',
    opened: '',
    credit_limit: '',
    annual_fee: '',
    apr_zero_pct_until: '',
    is_business: false,
  })
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setErr(null)
    try {
      await createCard({
        card_key: form.card_key,
        name: form.name,
        issuer: form.issuer || null,
        network: null,
        is_business: form.is_business,
        status: 'active',
        opened: form.opened || null,
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : null,
        apr_purchase: null,
        apr_zero_pct_until: form.apr_zero_pct_until || null,
        statement_day: null,
        annual_fee: form.annual_fee ? parseFloat(form.annual_fee) : null,
        af_due: null,
        points_program: form.points_program || null,
        velocity_family: null,
        notes: null,
        reward_categories: [],
        sub: null,
      })
      onSaved()
    } catch (e: any) {
      setErr(e?.message || 'failed')
    } finally {
      setSaving(false)
    }
  }

  const Inp = (key: keyof typeof form, placeholder: string, type = 'text') => (
    <input
      type={type}
      value={String(form[key] ?? '')}
      placeholder={placeholder}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-zinc-600"
    />
  )

  return (
    <form onSubmit={submit} className="border border-zinc-900 rounded p-4 grid grid-cols-2 gap-3">
      {Inp('card_key', 'short id (e.g. csr)')}
      {Inp('name', 'display name')}
      {Inp('issuer', 'issuer (chase, amex, …)')}
      {Inp('points_program', 'points program (ur, mr, …)')}
      {Inp('opened', 'opened YYYY-MM-DD')}
      {Inp('credit_limit', 'credit limit', 'number')}
      {Inp('annual_fee', 'annual fee', 'number')}
      {Inp('apr_zero_pct_until', '0% APR until YYYY-MM-DD')}
      <label className="flex items-center gap-2 col-span-2 text-sm text-zinc-400">
        <input
          type="checkbox"
          checked={form.is_business}
          onChange={(e) => setForm({ ...form, is_business: e.target.checked })}
        />
        business card
      </label>
      <div className="col-span-2 flex items-center justify-end gap-2">
        {err && <div className="text-red-400 text-xs mr-auto">{err}</div>}
        <button
          type="submit"
          disabled={saving}
          className="px-3 py-1.5 rounded bg-zinc-100 text-zinc-900 text-sm font-medium hover:bg-white disabled:opacity-40"
        >
          {saving ? '…' : 'save'}
        </button>
      </div>
    </form>
  )
}
